import {Injectable, SecurityContext} from '@angular/core';
import {DomSanitizer} from '@angular/platform-browser';
import jsPDF from 'jspdf';
import {User} from '../core/models/user/user.interface';
import {IdDescriptionPair} from '../core/models/user/users-query-result';
import autoTable from 'jspdf-autotable';
import {AccessPointOccupants} from '../core/models/access-point/access-point';
import {Network} from '../core/models/network/network.interface';
import * as ExcelJS from 'exceljs';
import {Workbook} from 'exceljs';
import {MonitoringService} from './logging.service';
import { HttpClient } from '@angular/common/http';
import { first } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class CommonService {

  private userIconPath = '../../../../../assets/images/user-icon.png';
  constructor(
    private domSanitizer: DomSanitizer,
    private monitoringService: MonitoringService,
    private http: HttpClient
  ) {}
  userTypeMapper: IdDescriptionPair[] = [];

  public async isLanguageFileExist(lang: string, prefix: string = '/assets/i18n/', suffix: string = '.json'): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.http.head(`${prefix}${lang}${suffix}`).pipe(first()).subscribe(res => {
        resolve(true);
      }, error => {
        reject(false);
      });
    });
  }

  public currentTimeInTicks = (): number => {
    const currentTime = Date.now();
    let epochMicroTimeDiff = Math.abs(new Date(0, 0, 1).setFullYear(1));
    return (currentTime + epochMicroTimeDiff) * 10000;
  }

  /**
   * Returns a boolean whether - a contains b (not case sensitive).
   * @param a String
   * @param b String
   * @param options Options object that contain a pipe callback to transform a OR b parameters before testing the case.
   */
  nonCaseSensitiveIncludes(a: string = '', b: string = '', options: any = {}): boolean {
    const {pipeA, pipeB} = options;

    if (options.pipeA) { a = pipeA(a); }
    if (options.pipeB) { b = pipeB(b); }

    return a.toLowerCase().includes(b.toLowerCase());
  }

  public compareByNameAscFn = (a, b) => {
    if (a.name < b.name) {
      return -1;
    }
    if (a.name > b.name) {
      return 1;
    }
    return 0;
  };

  public compareByDoorNameAscFn = (door1, door2): number => {

    const regex = /(^.+)\s(\d+)$/;

    const door1Match = door1.name.match(regex);
    const door2Match = door2.name.match(regex);

    if(door1Match && door2Match) {
      return door1Match[1] !== door2Match[1] ?
        this.comparison(door1.name, door2.name) :
        this.comparison(+door1Match[2], +door2Match[2]);
    }else {
      return this.comparison(door1.name, door2.name);
    }
  };

  private comparison = (a,b, ascent = true):number => {
    if ((typeof a === 'string' && a.startsWith('All')) || (typeof b === 'string' &&  b.startsWith('All'))) {
      return ascent ? 1 : -1;
    }
    if (a < b) {
      return ascent ? -1 : 1;
    }
    if (a > b) {
      return ascent ? 1 : -1;
    }
    return 0;
  };
  public compareByEmployeeNameAscFn = (a, b) => {
    if (a.employeeName.toLowerCase() < b.employeeName.toLowerCase()) {
      return -1;
    }
    if (a.employeeName.toLowerCase() > b.employeeName.toLowerCase()) {
      return 1;
    }
    return 0;
  };

  public compareByNameDescFn = (a, b) => {
    if (a.name < b.name) {
      return 1;
    }
    if (a.name > b.name) {
      return -1;
    }
    return 0;
  };

  public compareByFirstAndLastNameAscFn = (a, b) => {
    let aName = `${a.firstName} ${a.lastName}`.toLowerCase();
    if (!a.firstName) {
      aName = `${a.lastName}`.toLowerCase();
    }
    let bName = `${b.firstName} ${b.lastName}`.toLowerCase();
    if (!b.firstName) {
      bName = `${b.lastName}`.toLowerCase();
    }
    let newA = {...a, name: aName};
    let newB = {...b, name: bName};
    return this.compareByNameAscFn(newA, newB);
  };

  getUserType = (typeId: number) => {
    const userType = this.userTypeMapper.find((type) => type.id === typeId);
    return userType ? userType.description : 'unknown';
  };

  resolveUserPhoto(user: User | null): string {
    if (user?.imageBase64) {
      return user.imageBase64.includes('data:image/png;base64,')
        ? user.imageBase64
        : 'data:image/png;base64,' + user.imageBase64;
    }
    if (user?.photo) {
      return user.photo;
    }
    return this.userIconPath;
  }
  resolveOccupantsPhoto(user: AccessPointOccupants): string {
    if (user.imageBase64) {
      return 'data:image/png;base64,' + user.imageBase64;
    }
    return this.userIconPath;
  }

  public removeBase64PrefixFromImage(imageUrl: string): string {
    try {
      return imageUrl.split(',')[1];
    } catch (error) {
      this.monitoringService.logException(error);
      return '';
    }
  }

  public sanitizeImage(image: string): string {
    try {
      if (typeof image !== 'string') {
        return '';
      }
      atob(`${image}`);
      return this.domSanitizer.sanitize(
        SecurityContext.URL,
        this.domSanitizer.bypassSecurityTrustUrl(
          `data:image/png;base64,${image}`
        )
      );
    } catch (error) {
      this.monitoringService.logException(error);
      return this.userIconPath;
    }
  }

  public downloadPdf(data: any, fileName: string, isAttendance = false, filters = null) {
    if (!data.length) { return; }

    let doc = new jsPDF('l', 'pt', 'a4');
    doc.setFontSize(10);

    if(isAttendance){
      doc = this.pdfAttendanceRoutine(doc,data,filters);
    }else{
      doc = this.pdfRoutine(doc,data);
    }

    doc.save(`${fileName}.pdf`);
  }

  downloadFile(data: any, title: string, filename: string = 'data', isAttendance = false, filters = null) {
    if (!data.length) { return; }

    let csvData: string = '';

    if(filters){
      csvData += `Filters` + '\r\n' +
        `Search word: ${filters.search}` + ',' +
        'Employee name: ' + (filters.filter.employeeId !== undefined ? data[0][0].name: 'All users') + ',' +
        'Department name: ' + (filters.filter.departmentName !== undefined ? filters.filter.departmentName: 'All departments') + ',' +
        'Month: ' + (filters.filter.month !== undefined ? filters.filter.month: 'All months')+ '\r\n';
    }

    if(isAttendance){

      csvData += title + '\r\n';
      csvData += `Showing ${data.length} results` + '\r\n' + '\r\n';

      data.forEach(item => {

        item = this.removeEventObjFromListOfEventsForExport(item);

        csvData +=  'Name: '+ item[0].name + ',' + 'Department: ' + item[0].department +  '\r\n';

        const summary = `Hours: ` + item[0][`hours`] + ',' + `Working days: ` + item[0][`workingDays`] + ',' +
          `Absence days: ` + item[0][`absenceDays`] + '\r\n' + `Late days: ` + item[0][`lateDays`] + ',' +
          `Early leave: ` + item[0][`earlyLeave`] + ',' + `Auto Arrival: ` + item[0][`autoArrival`] + ',' +
          `Auto Arrival: ` + item[0][`autoLeave`] + ',' +
          `Average hours: ` + item[0][`averageHoursInSeconds`]  +  '\r\n' +  '\r\n';

        item = this.getClearedAttendanceObject(item);

        csvData += this.ConvertToCSV(item, Object.keys(item[0]), title, isAttendance);
        csvData += summary;

      });

    }else {
      data = this.removeEventObjFromListOfEventsForExport(data);

      csvData = this.ConvertToCSV(data, Object.keys(data[0]), title);
    }

    let blob = new Blob(['\ufeff' + csvData], {
      type: 'text/csv;charset=utf-8;',
    });
    this.saveToFile(blob,filename,'.csv');
  }

  ConvertToCSV(objArray, headerList, title: string, isAttendance: boolean = false) {


    let array = typeof objArray !== 'object' ? JSON.parse(objArray) : objArray;
    let str = '';
    let row = '';
    let newTitle = this.divideWordByUpperCase(title);
    if(!isAttendance){
      str += newTitle + '\r\n';
      str += `Showing ${array.length} results` + '\r\n';
    }
    for (let index in headerList) {
      let newValue = this.divideWordByUpperCase(headerList[index]);
      row += newValue + ',';
    }
    row = row.slice(0, -1);
    str += row + '\r\n';
    for (let item of array) {
      let line = '';
      for (let k = 0; k < headerList.length; k++) {
        let head = headerList[k];
        if (k > 0) {
          line += ',';
        }
        line += '"' + item[head] + '"';
      }
      str += line + '\r\n';
    }

    return str;
  }

  downloadHtml(data, filename = 'data', isAttendance = false, filters = null) {

    if (!data.length) { return; }

    let htmlData: string = '';

    if(isAttendance){
      htmlData = this.generateHmlFileHeader();

      if(filters){
        htmlData += this.generateFiltersHtml(filters,data);
      }

      data.forEach(item => {
        item = this.removeEventObjFromListOfEventsForExport(item);
        htmlData += this.generateTableOwnerInfoHtml(item);
        const summaryHtml = this.generateSummaryHtml(item);
        item = this.getClearedAttendanceObject(item);
        htmlData = this.generateHtmlTable(htmlData,item, Object.keys(item[0]));
        htmlData += summaryHtml;
      });
      htmlData += `</body></html>`;
    }else {
      data = this.removeEventObjFromListOfEventsForExport(data);
      htmlData = this.convertToHtml(data, Object.keys(data[0]));
    }


    let blob = new Blob(['\ufeff' + htmlData], {
      type: 'text/html;charset=utf-8;',
    });
    this.saveToFile(blob,filename,'.html');
  }

  private convertToHtml(objArray, headerList):string {
    let htmlData = this.generateHmlFileHeader();
    htmlData += this.generateHtmlTable(htmlData,objArray,headerList);
    htmlData += `</body></html>`;
    return htmlData;
  }

  downloadExcel(
    data: any,
    title: string,
    groupData: boolean,
    headersToDisplay: any[],
    propertyNames: { displayedName: string; name: string }[],
    additionalData: {
      filename: string;
      isAttendance: boolean;
      filters: any;
      isReport: boolean;
    } = {
      filename: 'data',
      isAttendance: false,
      filters: null,
      isReport: false
    }
  ) {
    if (!data.length) { return; }

    data = this.removeEventObjFromListOfEventsForExport(data);
    data = this.removeUnneededForExportFields(data);

    if (groupData) {

      let resultArray = [];

      if (additionalData.isAttendance) {
        resultArray = this.attendanceExportPrepare(data, propertyNames,additionalData.filters);
      } else {
        data.forEach(item => {
          let groupsObject = this.groupDataBy(item, propertyNames);
          let excelGroupedData = this.createExcelGroupedData(
            groupsObject,
            headersToDisplay,
            propertyNames
          );

          resultArray.push({data: excelGroupedData});
        });
      }

      this.exportJsonToExcelAlt(resultArray, title, additionalData.filename,additionalData.isAttendance,additionalData.isReport);
    } else {
      const excelData: Array<any> = [];
      let excelHeader = {};
      let headers = Object.keys(data[0]);
      let chars = this.genCharArray('A', 'Z');
      for (let i = 0; i < headers.length; i++) {
        excelHeader[chars[i]] = headers[i];
      }
      const udt: any = {
        data: [excelHeader],
        skipHeader: false,
      };
      for (const item of data) {
        let objectToPush = {};
        for (let index in headers) {
          let head = headers[index];
          objectToPush[chars[index]] = item[head];
        }
        udt.data.push(objectToPush);
      }
      excelData.push(udt);
      this.exportJsonToExcelAlt(excelData, title, additionalData.filename,additionalData.isAttendance,additionalData.isReport);
    }
  }

  private async exportJsonToExcelAlt(
    json: any[],
    title: string,
    fileName: string,
    isAttendance: boolean = false,
    isReport: boolean = false
  ) {
    let newTitle = this.divideWordByUpperCase(title);

    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'AxTraxPro';
    workbook.created = new Date();


    const worksheet = workbook.addWorksheet(
      isReport ?
        'sheet 1 Report' :
        newTitle.replace(/[^a-zA-Z ]/g, '')
    );

    const logoBase64: string = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAhwAAACqCAYAAADm8pY9AAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAF0lJREFUeNrs3ft12koewHFlz/1/2QoWV3DlCixXEFJBcAXBFZhUgFMBpAKcCiAV4FsBbAW4A68m/unesTKSZqQZvfh+zuHkAQg9RqPfvKMIAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAFDL6+trnL4SzgQAAPAZXMzT1yp9HV5/t1Wf4UwBAADb4GKavmYSXOxe3cw5gwCAvvvAKWg9uJikfyTpS9VO3Mifk4abvf7w4cMzZxcAQMBxucFFLAHGn/L3aYCfekoDjk+ccQAAAcdlBBiJBBVZcOGrj8U+ff1MX6oWQwUxqyhXK5IGHFxLAEBv/cEpqB1cZAHFTYDgQgUWf6k/TU0l6W+/pH9s88FO+tk9VwYAQMAx3OBiGr1vFkk8bfpZCy72Dv0wXrgqAAACjvEEGQ/paxY179SpnCS4+NU0Qm0EAICAg2Bjnv6xbrAJVQOhAoq/5E8VYLxcyLnLOspaBWHpeTldeFpzOV998MKIKNJazsXkbyDg8H1TJo7BhrrR/q65kJvvkh+iqlZoYZtRpa/rC09y6gGwG9D+qgD6lpyCtJbLN38VIOSl8sI9tbgg4LB7YFZluHq/i1OAmzcZcMlh7pIBqs63lJiBUZjKS+VfD9K5fZO+vl16TSbe/ItT8O5Bn90sOnXT3EVvk2spt+nrPn1tfN9EMp35WUoh6nUc0kyisq+u/V2+kPKAUVJ5wULysZU06YCAY3CBgXowL9PXQoIEnxF63icJLp497PdU9nuZ32+5GXe5B7b6+9rzMYb0ucZ3ZmREwOipwOPA+k8EHEMLNlQp+hC9NX2sQidiX+2Qso/Zfj9Ev9delI2GmQ3guqigKKlZCppxKwKjp/KIHUHH5RpiH46V4YGlHuCftIffTCttf22rj4D0vciaCL6nv/ukvf1gCCjUsWy04yh7KPfdl4bf3XA7Ap04qfyqwfdvJI+yCSQmEnTc0ncLfS9FxwUrpu70YCP33tm2SUIFDPkNu5TwDfuVaO8bWRzbu+0U7Wf+Mx1cm/NrM/GFpunkdVh25ESjS2s7z7+xssgPDlyRyzOoJhWJiE2jNvT/+2iIqOfaDTFPX0e5IeY1bqi5fPeYe8ibtqW//1y233Jsj4bPbPo+vKxmZ1GfNSQA+pFHq5F79+lfrwrys8yvfnicMQKOvrs3PLS/av/+n+E7J+3vqhljGv3TIdP6QSk1JWv5bvZ3U9Bjcmf4zH3uZlX/vpXjUa+79P/uBnBNPnvYBp1HgfEEHi+Sn5XlX1+45y/LHwNMyJs0karagJk8wJ9yw1M3UlqeaDULT7kajyj3b9u5LvLfneZ+Vz14Yy3IedRrMNL9vo7+6Rz6ZGrDlNqM/VCuR4POoqZzO4sury/Hi4frbdt+fsoF33XQ7g7X/FqtQbXgnscoqahZ2hITw3srrR1xnXuvsg9H+l9b7e1V0e+GjNz71Icjdz6bol3XX3owWXK2SCdt98uRfPFY8NtbrgzGfuPFpk6Ktp1G5XNxzzKOrgIO286iBzqPEnDg8gIO+e1FVcd5jN9FTm3edDgWawT8nYnMI7vOopvobX0FmzVqVHPYHWd33A++3H81msLfZlEy7tnOqTxgVVQA9D1EVpp6pyHSQS695dPy3keaJuBAmw/xbKTND9UG2uPd/Wj5uR9yI9oEHKrz6L2Pm1VK9DcOX/mWmzvF9SH6YPnxn+nvLEeejrNMWZ2XPyUwTQo+fhtZ9mORGrBsm3FkufqptrjYs6THpzppTJXUHdJ9dg8/Nnho2i4kqTppfupxIe9F+t6ZrtekQTrL0kCWHoyBhr4rDfLlG0l7Zdt/0L5zknT9o26+gp6UkIqmCPew3WDVfbmpzRPD+2tDjePaZj/bblIpmHfE5FhxfCZzT/sYO/YhOddJTxVt1MGbjfrQpCLnYC59eg6O5z2x2PbK8RzbcF42QPbFdc6ZuOY53Tn8xqxBOtm1lGcUHc/CMd9ZSF8657l/alzrpYc5hl4l7S4ZlTPAGgDDQyIuSJRzx2GviYcEupDXxOJmn1s+HJMeBhxLyxttpX1n1vbkUmVtx75+O9eRuMoiwLXoJOCQNFsnwHANOEJPjrZyPO5ZjYfNJND9ZSyU9DTgWNVJl1q+2jSdvTrmG+cAae1oExyiPwFHaU9nCTbOuc6Kpod/UjfgMHUaNZR83pWWC6L7s+WDcdnDgMO2tDn18b1AJavGQYHjw2cX6Fq0GnBIIH/wmAl3HXAY84maD8/GQY1jzZx1MNODgGPpmi4dA6/GAYfk47sW0pvTHFBjMaiJv0qqJvUL9yX3b/WdmV6tH8ny7zVLswf5/iGXicyj31d61ZsGkor9LmtPfunZdVDn0yYg2OfmSFFs2zJ9zjx653gOVzbV4K5t7NF4OsOuI8u+EwMSS55g+xBQE/OdHLa/sGz2UL/vMlT004A6J/63xndu2to5bcXuNgpvc8f0RsDRNsupzScVD/Z3QYBLSVpKYnpGu6j43aoH7Uvu/aJj61uHI9uZRU0LQn1zuCF9pZtTjYf92vIzthnGnSH4Qv+Cjq1lmlL3pWsnTZtS7YNlMP8r6BnYAmhFx+X7vlDb20dvI2OyWZuzWZyrgo3YYtsbyU/U9q4/CPn3rfzWxqKQY53e0F3purQPh6HaMN+0kW+Xm5R89zVfoi3pEDk1bDvRa2cM78/yNTi5JoezqVTUZZOKQ2fRc1Hm6lAdP/e871tf1eCOfUPWga9J200qviWejs+XpcO5cK3y35ZsK2jzXA+aVI6u19+ieeMozROLJnmgRRPZoU7fC23tLubHGXDQkY32MHYKlRtrLYkoNjzUd/KaVd2QBQko+75p22t5JaYoWutUOq3IGBLHjKOtgGPZ9CFrCBpb6fNQYzTJa8F1DNLGTsDR+PgO2r25a9Dp7+zYn8O1P8uiIG2eHfZv6jGd7FrKs537a5UEHN5GKFqkr6WH36gKaKYRLi6QCTosNvADpq2Aw/aBHVc8+G0z12kL5846YJB9d3nAxB2lhz4FHEd5cPw9JDz3mjge307bTmwRZM5rdAR0qeWYOgY3Z0Nnapf9m3tOJ20EHEUFlXPF93Z1glTHfdu1UctaUSu6jkDAQcBRq8r3aLGtdYghi55KG2WjoFYhHlojDTi8rACs1RzOGm5n7hIkeXyYGGtjan53GyCdtLGWyrnOgzZ0wFFR8xIi7ylr1mWODgIOAg7Lm8VpWKnDQ/IcKAN0rQafOdaOHDpOD50HHD29d1yCjjjQ/aE3C8QOtSONmuc6DDjKzsus44Bj5aNZzVOAs4hG7l8RYHmjRNrw4gqbqg/ImgYni21NfHcelREGdUat2JYu64xgQAtkqYC95cddH2quw68fIveRTi8DyzfWJfnGqQdTfhft27cQ51pGqhXljx/Hfv8NMuCQ0mbWHhyXRJLTLh7MRb/7+n5K9qTgu7tcW3Vf5juwfei7rFHx3fJznwPc+Go44VeHr0wcHgz3DIHtNdt0d+OYpuoEsrb399chLUAned2uIt/42vE+qnx6WrfQ1EDR1AAJzSr9S8jLsuo1w0xx69z3s/bgrWGUic0olVi+uzZ0/FrngoVJLkgq7ZBU0CHz/Go3TXoS+LzbdhadudzwDtXP00DH5XtWwW0H9wRNKu6FgmCjpGr0EWqleS50k4pWoDr4Orchm1RKmteOLaTBc+jOsH00xNViTStyqlkp91pJXL9oKlHpq/VttahWfe4/DokkmxxmopVOrrPEG/0+s6j692PJfq+ySLpk9s5Jbjud1ChFdpMRvbhUkapagHTb+8iu6lpd4/sAh6dKpIeowYqVmlM0ntlEh1YQyS9Rn9VInXJpbi/pzmftg6nkbnvPVN5TLaSpuGHQETveP31pciy6Pm3UJD0X5HtJS79PwGGRqdhMbX5TcEM8GRKZqg2JHWbry99YcUXinVRkXlXvmz7XBdsmjU2NbX+3DDjmIQIOefio7foYlja4NvYBBxjZMvUfXR54jhUvk5ppSi3F/kkC2abamE10ErUznXcWbNz25D4pajL7q4Xf/llwzv895vt2jFOb/6iIWPW/P7vczIaOjvuKqFj/7FON902faztjn0b2nUW/1bimm8iuo533zqO5fWjaee1xSG3sAw405tLEoF4rybR71+5do4+QieoP9Tiiy6fOydUApmNvY/9OJYVa9CzDyQ8Vm+Y+s5I2sqOhn4TTEvKG3y/7/kJ+8/z6+wqv+eFvZ8NMpzub9ts2+3A4zCx6aPAb65Bt6pb74DIZWWdDYAv2ffR9OKQP1PG1ZR72u+6qut6HZXYwRbx+LIua+xyyD0dn/Si6nma+K4Prw6FKo1q7vyoZ7/PVc+m/VRX5fcH31WcfG/x+4felNFL03nO631daicy0kqqqhl1oVX0/ow77bgjb5pR9gxvVtgrz12J7IUaAaNXgrjf8mFaB7WshQwVKDwPd/bvofb8vW59G0Dyn7lNV67np6bFMOqzhuEhD7DRaNZa5z/tduvKrvL/sy/46dBaNJFBqY+KaUJ1HfzWZpcf86Hgcp4Gt2Dm0YGMx4GAjK2iophWXWStPA33o7WXfVQHiaahDwzsOjkY9LJaJv1CmjxPRzANv37XDWHwJMwR2FGwkjg/qPj+IXUxbDrL2H/xQnUFVx+lH5qGpbTrmgyPgQFFmP2nh4V6rBNB0TY2KY67zgHvo0QRtY+IyckiVSjfRWxPGbfr6j/4kTP99Jf+vXl97fByZxdjnZIDRqGtL/+D6oqOahCZUv5IQUyKrOVrqVGlO5KFyTbLxFvzNHUp7KoB4LKsKlxJ3VupWTWcPLR2HCmDrBqNqgsErhlq3nvZimknDGOrU5nNtts/E8P5SejfvDCNB5k167edmEZwbtp397tLw3USb5XResP2pjHZZdjE1u+ZLj5PALMCy9apZpEmJMm5rddgLYducp6aRX/bxoSx5U5PmtiyQRbu1CUwvDuMD/7fptAumq4219wuXBK4aFitDW40rmRYMc9L3a1Y1TLFg3+cFgUuwoVwdDp9zWmnTZ4mmwZDYzqcmHuOw2BBLyLd5DA2HWVfmAZ7Tya7H+X3IYbG7psszNCngXOKw2EHVcEhwsKgojZtKRrOKUoQPphtAr0p9KNtvKbGbSjPrDvoHfB5AcvC5j2uP6WDNAkxeagZs7Ht8GD7T1Krj2s6xKqoVayO//W/B//8k4OgPm5vOdMFO2t/1ORO+uvSmlnY9fV6M+4LfMCXoqqnNyzLZWVsnuMedRX9LCz5KIlLqjz2n0VWENvyvpwHT3PM9S9NKGEUj0v5s4beL8pzTmE/4oDqNypj2F0PJQb9IGyn9Zhd0I1NXZ9vYRA3m8CiaVEwmJPusBQ7Pud95MmRCp4Lgo0u2wYa6Dt8C7cNHyyCgUedRKUmH6DyYXzAQYdz0bYekJiJEwPlrJVbVX4XL7s2+4P5PWijUFf0GnVV7dkPPcm2jB1MVtrTLx47bTpq24Rb9rnQGPeam+41z7xeJq/bTV98Bh+mj1wGv8Tz0svXSxm57rOca/VrObTWtjK0Ph8PxnGtuPw51DCX9Aoz9EGr084gDnNdL7cMxKTnPsw7yt3M0coMbpSKlxr/H1Kf/vjb1UFe1IV0MbSr6XdV0k770uQDeLWIkTTumKbLv2joOuYltH+DfAp7DTWS3oJtLjUzeyuFY72RhNpf5G1SwsaWIUIvtta+7oJ/tCKxnx/tn6VA6vpc05TotPn2E/OUzLyXXOOSkh59LalxwQbUnic+e9jX3YaotDjd1KKn4iPjXoUcH9GFfCkYMFVk1KMG+tjEL6QWPUvmtptBz7dnWYbuxw3Z3NdO6MU1Sw9Fo+4sQtUk179V5BAKOIexn0xuwonqxi4eoSwY+c9ju1KEa+2BYEXjqWA1+fg08ymikAcfWdZh0WfOaXLd1iGCxRvPcpMH3a9/vBByF1+7cxirQFdf5GF2Ai5vavGx5ecfvjy0adTme4J0hpRnp1LCK0sRluOKdYSVitU8ui8cxwqCeH46fV53/jhIk7iS4WMvfVWZ+jNyb3zYOvz1tkKbqrDi8pWnFSz5T1vk99tVXTa7VriSdfOdqDLumYmkq9eQi5oNrDUcuQt0ZSlHZ78aBj893DcfRdzWzh+Nc+Ow86ri9he8SODUcwdJkCGvLfZz52qbkGy62ntLJxdZwWNRyZDWd0wbbj2Ubhc3CBI/9DigWkhC3+YRnuKnOemKpmIW0aqbRwvcLqtpjQ+a0lX2fe37AJJ6313rv7YKMwMsDvUkbe81MqrVZSEcccMw6CjasRhg5poGj5TYPofoIEXCU/o5N3x6nSRi1pSx6Nzsx7C9i1dTmq7KbMj801aWGw5DBHCpKJ8uKzHNl2L6+Dsy6YMiv74DDtm373MH1tq1JOHrKyM+2pZkaQ2WDlGTGGnA4pk2fZp7TpnUHxBrT7PtIrxcfcLjmg7Jfy4LXzuEaLiP0OuAovWEsAo5YMoqtzfwWBTdttnDctKK6fln1wLO4uQ4hAw7HWoRVB9d71vRBUZAmvNTgOG47SJPUmAOOiodOCEvLfVr43mbNbVt3biTg6F2Ay4zEPQ82YouAI19CsJ6AqckoFUPtR74p51yWKbsMl/IccLhkbnFH1/1c92HuWAuxrrFvkxrV4DPP52fsAcekpQfBwiEfsk2Tu5rH7L2PEAGHU554DpjOzm02TffJoEaplIxcOOU+cx29TdKkRhNctbF0tfzGlfym+u3r3Dot+7L9jsonDJoG3HXrSZC6mEhNbCw/N8sFeS6Tb50it9En+nWvM3nTNIL1OU5fd3J9QtzL6tqrSQQfba9fZDfSqU7ayNw5HutDVwWCEaa3R3mGhBiNt5FnEsseDKiWo3Jqc48lRV/bzpeE81Obz9qu4XAs/S86vubOJb0Qbeyeaoq8lirHXsNhuI+WnkawHF07bzuOJJk1PNZZjeOZUMPhvfZw7aFGg0JGNLDF27IajPTCqZqEWP69D/yTqwallHxJ+Dq7UfL7rSLe9L3n6PdFy16isPNe2E7Xven4mt9blipPWu3GX1HxipBea29UqUiesdYBqsqAXFYrrjhmm+u4bznNBKntSP9QgVM29Fw91P+UWsCqoDGrIVUrSu8bXHOb439pWoqVPOEucqvhnEbF03UXpZNT1F/fo+oVwEOmN3XP7CX/UXn3jaSzpKJm61leP6nNQNlDoKjtLuuVPAsVqcrQ2l3uN2PLkkrC1QPe3UtTzgRaSmvMo4FaiWfpUE2WBSFJmwmOgAMAgHEEHYcGbcLbLAgh4AAAAGUP9EmN+RXKpsVdS6fCxNP+EXAAAAblA6eg9ME+jd46B6lOaVUdhVzso7cORapDo3OHRQku8j3Lb1voQAsAQC1/cApKorG3EQQbw8M+1oKQOsMpEz14kdENWRDyU4KQE1cAADCaZyqnoDkJQhItCJl62OyLHoBEb8P4XuT31GRW+TH+VwQpAAACjssKQCbRP00w2bhtH6NYsoAiH9Cc0mDjijMPACDgIAiZRv80wdxE/vqDKHdpwLHhLAMACDhgCkKyAKRJp9SnNNj4xNkEAABOQYgMoV1bzAfC8sYAgEGghmMYQUgSve8H8mt9FTqJAgAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAADa938BBgBWML9c45I9WgAAAABJRU5ErkJggg==';

    const logo = workbook.addImage({
      base64: logoBase64,
      extension: 'png',
    });
    worksheet.mergeCells('A1:C3');
    worksheet.addImage(logo, 'A1:C3');
    worksheet.getCell('A1').fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: {argb: '#1f2935'}
    };

    if (json && json.length > 0) {

      let currentRowIndex: number = 0;
      let firstPass: boolean = true;

      json.forEach((item) => {
        let dataArray = item.data;

        const columns = [];
        let newRow = {};

        let keys = Object.keys(dataArray[0]);
        keys.forEach((key) => {
          dataArray[0][key] = this.divideWordByUpperCase(dataArray[0][key]);
          columns.push({header: '', key: dataArray[0][key], width: 20});
          newRow[dataArray[0][key]] = '';
        });

        worksheet.columns = columns;

        if (firstPass) {
          currentRowIndex += 4;
        } else {
          currentRowIndex += 1;
        }
        let row = worksheet.getRow(currentRowIndex);

        if (firstPass) {
          row.getCell(1).value = isReport ? 'Reports' : newTitle;
          row.getCell(1).font = {
            size: 26,
            bold: true
          };
        }

        if (dataArray && dataArray.length >= 0) {
          if (firstPass) {

            if (!isAttendance) {
              currentRowIndex += 1;
              row = worksheet.getRow(currentRowIndex);
              if (title.toLowerCase() === 'networks') {
                row.getCell(1).value = `Showing ${dataArray.length - 1} out of ${dataArray.length - 1}  results`;
              } else {
                row.getCell(1).value = `Showing ${isAttendance ? json.length - 1 : dataArray.length - 1} results`;
              }
            }

            firstPass = false;
          }
          currentRowIndex += 1;
          for (let i = 0, length = dataArray.length; i < length; i++, currentRowIndex++) {

            row = worksheet.getRow(currentRowIndex);
            let keys = Object.keys(dataArray[i]);
            let colNum: number = 1;
            keys.forEach(key => {
              row.getCell(colNum).value = this.capitalizeFirstLetter(dataArray[i][key]);
              row.getCell(colNum).border = {
                top: {style: 'thin'},
                left: {style: 'thin'},
                bottom: {style: 'thin'},
                right: {style: 'thin'}
              };
              row.getCell(colNum).alignment = { vertical: 'middle', horizontal: 'left' };
              colNum++;
            });

            if (i === 0) {
              row.font = {
                bold: true
              };
            }
          }
        }
      });

    }

    await this.saveXlsxToFile(fileName, workbook);
  }

  private async saveXlsxToFile(filename: string, workbook: Workbook): Promise<void> {
    const buffer = await workbook.xlsx.writeBuffer();
    const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

    const blob = new Blob([buffer], {type: fileType});
    this.saveToFile(blob,filename);
  }

  private saveToFile(blob: Blob, filename: string, fileExtension: string = '.xlsx'):void{
    let dwldLink = document.createElement('a');
    let url = URL.createObjectURL(blob);
    let isSafariBrowser =
      navigator.userAgent.indexOf('Safari') !== -1 &&
      navigator.userAgent.indexOf('Chrome') === -1;
    if (isSafariBrowser) {
      //if Safari open in new window to save file with random filename.
      dwldLink.setAttribute('target', '_blank');
    }
    dwldLink.setAttribute('href', url);
    dwldLink.setAttribute('download', filename + fileExtension);
    dwldLink.style.visibility = 'hidden';
    document.body.appendChild(dwldLink);
    dwldLink.click();
    document.body.removeChild(dwldLink);
  }

  private createExcelGroupedData(
    groupsObject: any,
    headersToDisplay: {
      displayedName: string;
      name: string;
      insertHeaderIndex: number;
      insertRowIndex: number;
    }[],
    propertyNames: { displayedName: string; name: string }[]
  ) {
    let excelData = [];
    let chars = this.genCharArray('A', 'Z');
    let excelHeaderRowObject = {};
    chars.forEach((char, index) => {
      let headerToDisplay = headersToDisplay.find(
        (elem) => elem.insertHeaderIndex === index
      );
      if (headerToDisplay) {
        excelHeaderRowObject[char] = headerToDisplay.displayedName;
      } else {
        excelHeaderRowObject[char] = '';
      }
    });
    excelData.push(excelHeaderRowObject);
    this.groupedDataToExcel(
      excelData,
      groupsObject,
      headersToDisplay,
      propertyNames
    );
    return excelData;
  }

  private groupedDataToExcel(
    excelData: any[],
    groupsObject: any,
    headersToDisplay: {
      displayedName: string;
      name: string;
      insertHeaderIndex: number;
      insertRowIndex: number;
    }[],
    propertyNames: { displayedName: string; name: string }[]
  ) {
    let groupsObjectKeys = Object.keys(groupsObject);
    let chars = this.genCharArray('A', 'Z');
    groupsObjectKeys.forEach((groupsObjectKey, index) => {
      if (
        groupsObject[groupsObjectKey] &&
        groupsObject[groupsObjectKey].length > 0
      ) {
        let excelRowObject = {};
        excelRowObject[chars[0]] = '';
        let displayedNameProperty =
          propertyNames[1].displayedName;
        excelRowObject[
          chars[2]
          ] = `${displayedNameProperty} ${groupsObjectKey}`;
        excelData.push(excelRowObject);
        for (let item of groupsObject[`${groupsObjectKey}`]) {
          excelRowObject = {};
          chars.forEach((char, index) => {
            let headerToDisplay = headersToDisplay.find(
              (elem) => elem.insertRowIndex === index
            );
            if (headerToDisplay) {
              excelRowObject[char] = item[headerToDisplay.name];
            } else {
              excelRowObject[char] = '';
            }
          });
          excelData.push(excelRowObject);
        }
      } else {
        let excelRowObject = {};
        let displayedNameProperty = propertyNames[0].displayedName;
        excelRowObject[
          chars[0]
          ] = `${displayedNameProperty} ${groupsObjectKey}`;
        excelData.push(excelRowObject);
        this.groupedDataToExcel(
          excelData,
          groupsObject[`${groupsObjectKey}`],
          headersToDisplay,
          propertyNames
        );
      }
    });
  }

  private groupDataBy(
    data: any,
    propertyNames: { displayedName: string; name: string }[]
  ): {} {
    let propertyNamesCopy = [...propertyNames];
    if (propertyNames && propertyNames.length > 0) {
      let groups = data.reduce((r, a) => {
        r[a[`${propertyNames[0].name}`]] = [
          ...(r[a[`${propertyNames[0].name}`]] || []),
          a,
        ];
        return r;
      }, {});
      propertyNamesCopy.shift();
      if (propertyNamesCopy.length === 0) {
        return groups;
      }
      let keys = Object.keys(groups);
      keys.forEach((key) => {
        groups[key] = this.groupDataBy(groups[`${key}`], propertyNamesCopy);
      });
      return groups;
    }
  }

  private getOptions(json: any, origin?: number): any {
    const options = {
      skipHeader: true,
      origin: -1,
      header: [],
    };
    options.skipHeader = json.skipHeader ? json.skipHeader : false;
    if (!options.skipHeader && json.header && json.header.length) {
      options.header = json.header;
    }
    if (origin) {
      options.origin = origin;
    }
    return options;
  }

  private genCharArray(charA, charZ) {
    let a = [];
    let i = charA.charCodeAt(0);
    let j = charZ.charCodeAt(0);
    for (; i <= j; ++i) {
      a.push(String.fromCharCode(i));
    }
    return a;
  }

  public divideWordByUpperCase(word: string) {
    let arrayOfWords = word.split(/(?=[A-Z])/);
    let newUpperCaseArrayOfWords: string[] = [];
    arrayOfWords.forEach((word) => {
      let newWord = word.charAt(0).toUpperCase() + word.slice(1);
      newUpperCaseArrayOfWords.push(newWord);
    });
    return newUpperCaseArrayOfWords.join(' ');
  }

  private removeEventObjFromListOfEventsForExport(data: [any]) {
    for (const item of data) {
      delete item.event;
      delete item.alert;
      delete item.alarm;
      delete item.message;
      delete item.timeFrom;
    }
    return data;
  }

  mapNetworksExportData(data: Network[]) {
    if (!data.length) { return []; }

    return data.map(el => ({
      status: el.controllersStatus || '',
      type: el.typeName || '',
      name: el.name || '',
      controllers: el.controllersCount || '',
      address: el.address || '',
      controllersStatus: el.controllersStatus || '',
    }));
  }
  public capitalizeFirstLetter(string) {

    if (typeof string !== 'string') { return string; }

    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  private removeUnneededForExportFields(data: []) {

    const unnecessaryFields = ['id', 'eventTypeId'];

    return data.map(item => {

      unnecessaryFields.forEach(fieldName => {
        delete item[fieldName];
      });

      return item;
    });

  }

  private attendanceExportPrepare(data: any, propertyNames: { displayedName: string; name: string }[], filters: any) {

    let resultArray = [];
    const newData = {data: []};

    newData.data.push( {A: 'Filters'});
    let filterData = {
      A: `Search word: ${filters.search}`,
      B: 'Employee name: ' + (filters.filter.employeeId !== undefined ? data[0][0].name: 'All users'),
      C: 'Department name: ' + (filters.filter.departmentName !== undefined ? filters.filter.departmentName: 'All departments'),
      D: 'Month: ' + (filters.filter.month !== undefined ? filters.filter.month: 'All months'),
    };

    newData.data.push(filterData);
    resultArray.push(newData);
    data.forEach(dataItem => {

      const newData = {data: []};
      newData.data.push({   // adding headers
        A: '',
        B: '',
        C: 'Date',
        D: 'Day',
        E: 'Entry',
        F: 'Exit',
        G: 'Duration',
        H: 'Comments'
      });

      propertyNames.forEach(propName => {
        if (['department', 'name'].includes(propName.name)) {
          newData.data.push({A: `${propName.displayedName} ` + dataItem[0][`${propName.name}`]});
        }
      });

      let searchedNames = propertyNames.map(propName => {
        return propName.name;
      });

      let newDataArray = dataItem.map(item => {
        let substItem = {...item};
        for (const [key] of Object.entries(substItem)) {

          if (searchedNames.includes(key)) {
            delete substItem[`${key}`];
          }
        }
        return substItem;

      });

      newDataArray.forEach(item => {
        newData.data.push({   // field data
          A: '',
          B: '',
          C: item.date,
          D: item.day,
          E: item.entry,
          F: item.exit,
          G: item.durationInHours,
          H: item.comments
        });
      });

      newData.data.push(
        {
          A: `Hours: ` + dataItem[0][`hours`],
          B: `Working days: ` + dataItem[0][`workingDays`],
          C: `Absence days: ` + dataItem[0][`absenceDays`]
        }
      );

      newData.data.push(
        {
          A: `Late days: ` + dataItem[0][`lateDays`],
          B: `Early leave: ` + dataItem[0][`earlyLeave`],
          C: `Auto arrival: ` + dataItem[0][`autoArrival`],
          D: `Auto leave: ` + dataItem[0][`autoLeave`],
          E: `Average hours: ` + dataItem[0][`averageHoursInSeconds`],
        }
      );
      resultArray.push(newData);
    });
    return resultArray;
  }

  private pdfRoutine(doc: jsPDF, data): jsPDF{

    data = this.removeEventObjFromListOfEventsForExport(data);

    let headerList = Object.keys(data[0]);

    headerList.forEach((item, index) => {
      headerList[index] = this.capitalizeFirstLetter(item);
    });

    let bodyList = [];
    data.forEach((element) => {
      let values = Object.values(element);
      bodyList.push(values);
    });

    autoTable(doc, {
      head: [headerList],
      body: bodyList,
    });

    return doc;
  }

  private pdfAttendanceRoutine(doc: jsPDF, data, filters = null){
    if(filters){
      let headerList = ['Filters:'];
      autoTable(doc, {
        head: [headerList],
        body: [],
      });

      headerList = ['Search word', 'Employee name','Department name','Month' ];
      let bodyList = [];
      bodyList.push([
        filters.search,
        filters.filter.employeeId !== undefined ? data[0][0].name: 'All users',
        filters.filter.departmentName !== undefined ? filters.filter.departmentName: 'All departments',
        filters.filter.month !== undefined ? filters.filter.month: 'All months'
      ]);

      autoTable(doc, {
        head: [headerList],
        body: bodyList,
      });
    }

    data.forEach(item => {

      item = this.removeEventObjFromListOfEventsForExport(item);


      let headerList = ['Name','Department'];
      let bodyList = [];
      bodyList.push([item[0].name,item[0].department]);

      autoTable(doc, {
        head: [headerList],
        body: bodyList,
      });

      const bodySummaryList = [];
      bodySummaryList.push([
        item[0][`hours`],
        item[0][`workingDays`],
        item[0][`absenceDays`],
        item[0][`lateDays`],
        item[0][`earlyLeave`],
        item[0][`autoArrival`],
        item[0][`autoLeave`],
        item[0][`averageHoursInSeconds`]
      ]);

      item = this.getClearedAttendanceObject(item);

      headerList = Object.keys(item[0]);
      headerList.forEach((item, index) => {
        headerList[index] = this.capitalizeFirstLetter(item);
      });
      bodyList = [];

      item.forEach((element) => {
        let values = Object.values(element);
        bodyList.push(values);
      });

      autoTable(doc, {
        head: [headerList],
        body: bodyList,
      });


      headerList = ['Summary:'];
      autoTable(doc, {
        head: [headerList],
        body: [],
      });


      headerList = ['Hours','Working days','Absence days', 'Late days', 'Early leave',
        'Auto Arrival', 'Auto Leave', 'Average hours'];
      autoTable(doc, {
        head: [headerList],
        body: bodySummaryList,
      });

    });

    return doc;
  }

  private getClearedAttendanceObject(item){

    return  item.map(subItem => {
      return {
        date: subItem.date,
        day: subItem.day,
        entry: subItem.entry,
        exit: subItem.exit,
        duration: subItem.durationInHours,
        comment: subItem.comments
      };
    });
  }

  private generateHmlFileHeader(): string{
    return `<!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>File</title>
      <style>
        table {
          width: 100%;
          border-collapse: collapse;
        }

        table, td, th {
          border: 1px solid black;
        }
        .title {
            font-weight: 700;
        }
      </style>
    </head>
    <body>`;
  }

  private generateHtmlTable(str,objArray,headerList):string {
    let array = typeof objArray !== 'object' ? JSON.parse(objArray) : objArray;
    let row = `<br>
    <table>
    <thead>
      <tr>`;
    for (let index in headerList) {
      row += `<th>${this.capitalizeFirstLetter(headerList[index])}</th>`;
    }
    row += `</tr>
    </thead>
    <tbody>`;
    str += row;
    for (let item of array) {
      let line = '';
      for (let index in headerList) {
        let head = headerList[index];
        line += `<td>${item[head]}</td>`;
      }
      str += line + '</tr>';
    }

    str += `</tbody>
    </table>`;

    return str;
  }

  private generateTableOwnerInfoHtml(item):string {
    return `<br>
<div>
    <span class="title">User name: </span><span>${item[0].name} </span>
    <span class="title">Department: </span><span>${item[0].department} </span>
</div>`;
  }

  private generateSummaryHtml(item):string {
    return `<br>
<div>
  <span>Hours: ${item[0][`hours`]} | </span>
  <span>Working days: ${item[0][`workingDays`]} | </span>
  <span>Absence days: ${item[0][`absenceDays`]}</span>
<div/>
<div>
  <span>Late days: ${item[0][`lateDays`]} | </span>
  <span>Early leave: ${item[0][`earlyLeave`]} | </span>
  <span>Auto arrival: ${item[0][`autoArrival`]} | </span>
  <span>Auto leave: ${item[0][`autoLeave`]} | </span>
  <span>Average hours: ${item[0][`averageHoursInSeconds`]}</span>
<div/>
<br>`;
  }

  private generateFiltersHtml(filters: any, data):string {
    return `<br>
        <div style="border: 1px solid black">
<div class="title">
    Filters
</div>
<div>
    <span class="title">Search word: </span>
    <span>${filters.search}</span> |
    <span class="title">Employee name: </span>
    <span>${filters.filter.employeeId !== undefined ? data[0][0].name : 'All users'}</span>  |
    <span class="title">Department name: </span>
    <span>${filters.filter.departmentName !== undefined ? filters.filter.departmentName : 'All departments'}</span>  |
    <span class="title">Month: </span>
    <span>${filters.filter.month !== undefined ? filters.filter.month : 'All months'}</span>
</div>
</div>`;
  }
}
